import { XIcon } from "lucide-react";
import { signIn } from "next-auth/react";
import { useState, useEffect } from "react";
import { Trans } from "next-i18next";

import { useLocale } from "@calcom/lib/hooks/useLocale";
import { trpc } from "@calcom/trpc/react";
import { Button, Dialog, DialogHeader, DialogContent, TextField, showToast, Logo } from "@calcom/ui";

import { REGEXP_ONLY_DIGITS } from "input-otp";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "./OtpField";

interface OtpLoginModalProps {
  isOpen: boolean;

  onClose: () => void;
  onSuccess: () => void;
}

export function OtpLoginModal({ isOpen, onClose, onSuccess }: OtpLoginModalProps) {
  const { t, i18n } = useLocale();

  const [email, setEmail] = useState("");

  const [otp, setOtp] = useState("");

  const [step, setStep] = useState<"EMAIL" | "OTP">("EMAIL");

  const [loading, setLoading] = useState(false);

  const [countdown, setCountdown] = useState(0);

  const [lastEmailUsed, setLastEmailUsed] = useState("");

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);

      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const sendOtp = trpc.viewer.auth.sendOtp.useMutation({
    onSuccess: () => {
      setStep("OTP");

      setCountdown(30);

      setLoading(false);

      showToast(t("verification_code_sent"), "success");
    },

    onError: (err) => {
      setLoading(false);

      if (err.data?.code === "TOO_MANY_REQUESTS") {
        showToast(t("too_many_requests"), "error");

        setStep("OTP");
      } else {
        showToast(t("failed_to_send_code"), "error");
      }
    },
  });

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);

    const normalizedEmail = email.toLowerCase().trim();

    if (normalizedEmail !== lastEmailUsed) {
      setCountdown(0);
      setLastEmailUsed(normalizedEmail);
    }

    sendOtp.mutate({ email: normalizedEmail, language: i18n.language });
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);

    const res = await signIn("otp", {
      email: email.toLowerCase().trim(),

      otpCode: otp.trim(),

      redirect: false,
    });

    if (res?.error) {
      showToast(t("wrong_code"), "error");

      setLoading(false);
    } else {
      onSuccess();
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);

    sendOtp.mutate({ email: email.toLowerCase().trim(), language: i18n.language });
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) return;
      }}>
      <DialogContent>
        <button
          onClick={() => {
            setStep("EMAIL");
            setEmail("");
            setOtp("");
            onClose();
          }}
          aria-label={t("close")}
          className="absolute right-2 top-2 rounded-md p-1 text-neutral-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none">
          <XIcon className="h-5 w-5" />
        </button>

        {step === "EMAIL" ? (
          <>
            <DialogHeader title={t("sign_in")} subtitle={t("sign_in_subtitle")} />
            <form onSubmit={handleEmailSubmit}>
              <TextField
                label={t("email_address")}
                name="email"
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <Button type="submit" color="primary" className="mt-6 w-full justify-center" loading={loading}>
                {t("continue")}
              </Button>
            </form>
          </>
        ) : (
          <>
            <DialogHeader title={t("otp_sent")} subtitle={t("otp_sent_subtitle")} />
            <form onSubmit={handleOtpSubmit}>
              <label className="mb-2 block text-sm font-medium leading-none text-gray-700" htmlFor="otp-verification">
                {t("otp_code")}
              </label>
              <InputOTP
                id="otp-verification"
                className="mx-auto"
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                value={otp}
                onChange={(v) => setOtp(v)}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>

              {/* <TextField
                    label={t("enter_6_digit_code")}
                    type="text"
                    required
                    autoFocus
                    maxLength={6}
                    placeholder="000000"
                    className="text-center font-mono text-xl tracking-[0.4em]" /> */}

              <Button type="submit" color="primary" className="mt-6 w-full justify-center" loading={loading}>
                {t("verify_and_login")}
              </Button>
            </form>

            <button
              type="button"
              disabled={countdown > 0 || loading}
              className="text-brand-500 my-4 w-full text-center text-sm font-medium hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleResendOtp}>
              {countdown > 0 ? `${t("resend_code_in")} ${countdown}s` : t("resend_code")}
            </button>
          </>
        )}

        <p className="mt-8 text-center text-xs leading-relaxed text-gray-500">
          <Trans t={t} i18nKey="sign_up_agreement">
            {"By signing up, you agree to our "}
            <a
              href="https://www.luckybrunch.de/allgemeine-geschaftsbedingungen"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-700 underline underline-offset-2">
              General Terms and Conditions
            </a>
            {" and our "}
            <a
              href="https://www.luckybrunch.de/datenschutzerklaerung"
              target="_blank"
              rel="noopener noreferrer"
              // className="font-medium text-gray-600 underline decoration-gray-300 hover:text-gray-900"
              className="text-brand-700 underline underline-offset-2">
              Privacy Policy
            </a>
            {"."}
          </Trans>
        </p>
      </DialogContent>
    </Dialog>
  );
}
